<script setup lang="ts">
import type {
  HouseDetailsAttributes,
  HousePhotoAttributes,
} from 'lc-services/types'

const { t } = useI18n({ useScope: 'local' })
const { query } = useRoute()
const { trackEvent } = useTracking()
const { locale } = useI18n()
const localePath = useLocalePath()

const { house, housePhotos } = defineProps<{
  house: HouseDetailsAttributes
  housePhotos: Array<HousePhotoAttributes & { hash: string }>
  totalCount: number
}>()

const toPhotoPageFromHash = (hash?: string) =>
  localePath({
    ...(hash ? { hash: '#' + hash } : null),
    name: 'house-photos',
    params: { slug: house.slug[locale.value] },
    query,
  })

const onClickSeeAllPhotos = () => {
  trackEvent({
    category: 'Product page',
    event: 'photo_grid_viewed',
    source: 'product page',
    house_id: Number(house.id),
  })
}
</script>

<template>
  <div class="flex flex-col gap-5 sm:gap-4">
    <h2 class="mb-0 text-2xl font-bold sm:mb-2">
      {{ t('title') }}
    </h2>
    <div class="grid grid-cols-2 gap-2">
      <NuxtLink
        v-for="(photo, i) in housePhotos.slice(1, 6)"
        :key="`photo-${photo.id}`"
        :to="toPhotoPageFromHash(photo.hash)"
        class="first:col-span-2"
        prefetch
        prefetch-on="visibility"
      >
        <BaseNuxtImg
          :alt="house.name"
          height="286"
          :src="photo.url"
          :width="i === 0 ? '693' : '352'"
        />
      </NuxtLink>
    </div>

    <BaseButton
      class="!text-md !font-bold sm:w-fit"
      color="secondary"
      prefetch
      variant="outline"
      :to="toPhotoPageFromHash()"
      @click.passive="onClickSeeAllPhotos"
    >
      {{ t('seeAllPhotos') }}
    </BaseButton>
  </div>
</template>

<i18n lang="yaml">
en:
  seeAllPhotos: View all photos
  title: Overview of the house
fr:
  seeAllPhotos: Voir toutes les photos
  title: Aperçu de la propriété
</i18n>
